import { VISIT_REASON_PATHS } from "@/constants";
import { DigitalVisitReasonEnum } from "@/lib/__generated/api";
import { DataLayerStepNames } from "@/lib/constants";
import enUsMessages from "@/messages/en-us.json";
import type { PatientData, SchedulerContext } from "@/types/scheduler-machine";

import { dataLayerPush } from "./dataLayerHelpers";
import { getBaseDataLayerParams } from "./dataLayerParams";

type FormErrorType =
  | "required_field"
  | "disallowed_character"
  | "max_length"
  | "offensive_input"
  | "invalid_date"
  | "format"
  | "age_ineligible";

export type FormValidationErrorItem = {
  field: string;
  type: FormErrorType;
  code: string;
  message: string;
  questionId?: string;
};

type StepValidationErrorType = "nbq_incomplete" | "age_ineligible";

type ErrorRule = { type: FormErrorType; rule: string };

const FIELD_NAMES: Record<keyof PatientData, string> = {
  firstName: "first_name",
  lastName: "last_name",
  dateOfBirth: "date_of_birth",
  emailAddress: "email_address",
  phoneNumber: "phone_number",
  dentalInsurance: "dental_insurance",
  smsMarketingConsent: "sms_marketing",
  smsTransactionalConsent: "sms_transactional",
};

const REQUIRED: ErrorRule = { type: "required_field", rule: "required" };
const INVALID_FORMAT: ErrorRule = { type: "format", rule: "invalid_format" };

const ERROR_RULES: Record<string, ErrorRule> = {
  firstNameRequired: REQUIRED,
  lastNameRequired: REQUIRED,
  firstNameMax: { type: "max_length", rule: "max_length" },
  offensiveInput: { type: "offensive_input", rule: "offensive_input" },
  dobRequired: REQUIRED,
  dobFormat: { type: "invalid_date", rule: "invalid_format" },
  dobAgeP: { type: "invalid_date", rule: "out_of_range" },
  phoneRequired: REQUIRED,
  phoneInvalid: INVALID_FORMAT,
  emailRequired: REQUIRED,
  emailInvalid: INVALID_FORMAT,
  insuranceRequired: REQUIRED,
};

const NAME_REQUIRED_KEYS = new Set(["firstNameRequired", "lastNameRequired"]);

const ENGLISH_ERROR_MESSAGES: Record<string, string> = {
  ...enUsMessages.Validation.PatientData,
  ...enUsMessages.Validation.ContactInformation,
  ...enUsMessages.Validation.Questionnaire,
  age_ineligible: enUsMessages.ErrorDialog.ageIneligibleBody,
};

function getEnglishErrorMessage(key: string) {
  return ENGLISH_ERROR_MESSAGES[key] ?? "Validation error";
}

function getErrorRule(key: string, value: unknown): ErrorRule {
  // Name fields share one regex for "empty" and "non-letter characters"; the value
  // is only inspected here to tell those apart and is never sent to the dataLayer.
  if (NAME_REQUIRED_KEYS.has(key) && typeof value === "string" && value.trim() !== "") {
    return { type: "disallowed_character", rule: "disallowed_character" };
  }
  return ERROR_RULES[key] ?? INVALID_FORMAT;
}

export function buildFieldValidationErrors(
  fieldMessages: Record<string, string>,
  data: Partial<PatientData>,
): FormValidationErrorItem[] {
  return Object.entries(fieldMessages).map(([field, key]) => {
    const fieldName = FIELD_NAMES[field as keyof PatientData] ?? field;
    const { type, rule } = getErrorRule(key, data[field as keyof PatientData]);
    return {
      field: fieldName,
      type,
      code: `${fieldName}_${rule}`,
      message: getEnglishErrorMessage(key),
    };
  });
}

export function buildStepValidationError(
  errorType: StepValidationErrorType,
): FormValidationErrorItem {
  if (errorType === "nbq_incomplete") {
    return {
      field: "needs_based_questions",
      type: "required_field",
      code: "needs_based_questions_required",
      message: getEnglishErrorMessage("pleaseSelectOneAnswer"),
    };
  }
  return {
    field: FIELD_NAMES.dateOfBirth,
    type: "age_ineligible",
    code: "date_of_birth_age_ineligible",
    message: getEnglishErrorMessage(errorType),
  };
}

type QuestionnaireQuestion = NonNullable<
  NonNullable<NonNullable<SchedulerContext["questionnaire"]>["questionnaireBy"]>["questions"]
>[number];

function getValidatableQuestions(context: SchedulerContext): QuestionnaireQuestion[] {
  const questions = context.questionnaire?.questionnaireBy?.questions ?? [];
  const rootQuestions = questions.filter((question) => question.triggers === null);
  const selectedAnswerIds = Object.values(context.needBasedQuestionsAnswers ?? {}).flat();
  const triggeredQuestions = selectedAnswerIds.flatMap((answerId) =>
    rootQuestions.flatMap((rootQuestion) => {
      const triggeredQuestion = questions.find((question) =>
        question.triggers?.some(
          (trigger) =>
            trigger.questionId === rootQuestion.questionId && trigger.answerId === answerId,
        ),
      );
      return triggeredQuestion ? [triggeredQuestion] : [];
    }),
  );

  return [...rootQuestions, ...triggeredQuestions].filter(
    (question) =>
      question.answer?.__typename === "MultiChoiceMultiAnswer" ||
      question.answer?.__typename === "MultiChoiceSingleAnswer",
  );
}

export function buildNeedsBasedQuestionValidationErrors(
  context: SchedulerContext,
): FormValidationErrorItem[] {
  const selectedAnswerIds = new Set(Object.values(context.needBasedQuestionsAnswers ?? {}).flat());

  return getValidatableQuestions(context)
    .filter((question) => {
      const answers =
        question.answer?.__typename === "MultiChoiceMultiAnswer" ||
        question.answer?.__typename === "MultiChoiceSingleAnswer"
          ? (question.answer.answers ?? [])
          : [];
      return !answers.some((answer) => selectedAnswerIds.has(answer.answerId ?? ""));
    })
    .flatMap((question) =>
      question.questionId
        ? [
            {
              ...buildStepValidationError("nbq_incomplete"),
              questionId: question.questionId,
            },
          ]
        : [],
    );
}

function getRfvScheduler(initialReason: DigitalVisitReasonEnum | null) {
  if (!initialReason) return "main";
  return VISIT_REASON_PATHS[initialReason]?.[0] ?? initialReason.toLowerCase();
}

// randomUUID is only available in secure contexts (HTTPS/localhost).
function createAttemptId() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  );
}

export function pushFormValidationError(
  errors: FormValidationErrorItem[],
  context: SchedulerContext,
) {
  const common = {
    validation_attempt_id: createAttemptId(),
    form_title: "scheduler 2.0 experience",
    form_description: context.initialReason ? "rfv_scheduler" : "main_scheduler",
    rfv_scheduler: getRfvScheduler(context.initialReason),
    form_type: "scheduler",
    form_result: "error_encountered",
    form_step: DataLayerStepNames.patientInformation,
    ...getBaseDataLayerParams(context),
  };

  dataLayerPush(
    { event: "form_validation_error", ...common, form_error_count: errors.length },
    context.stateCode,
  );

  errors.forEach((error, index) => {
    dataLayerPush(
      {
        event: "form_field_validation_error",
        ...common,
        error_index: index + 1,
        error_field: error.field,
        error_type: error.type,
        error_code: error.code,
        error_message: error.message,
        ...(error.questionId && { error_question_id: error.questionId }),
      },
      context.stateCode,
    );
  });
}
